import React, { useState, useMemo } from 'react';
import { useRestoMod } from '../../context/RestoModContext';
import {
  PhotoLog,
  PhotoCategory,
  RestorationPhase,
  RESTORATION_PHASES,
} from '../../types/teardown';
import { MechanicalButton } from '../common/MechanicalButton';
import {
  Camera,
  Plus,
  Search,
  Filter,
  ShieldCheck,
  CheckCircle2,
  Trash2,
  Edit2,
  Maximize2,
  X,
  Tag,
  Layers,
} from 'lucide-react';

export interface PhotoLogDrawerProps {
  phaseFilter?: RestorationPhase;
  subAssemblyFilter?: string;
  isOpen?: boolean;
  onClose?: () => void;
  className?: string;
}

const CATEGORY_CONFIG: Record<PhotoCategory, { label: string; badgeClass: string }> = {
  before_after_pair: {
    label: 'Before / After Pair',
    badgeClass: 'bg-safety-amber/20 text-safety-amber border-safety-amber/40',
  },
  teardown_as_found: {
    label: 'As-Found / Barn Find',
    badgeClass: 'bg-shop-well text-shop-muted border-shop-border',
  },
  disassembly_routing: {
    label: 'Disassembly Routing',
    badgeClass: 'bg-safety-blue/20 text-safety-blue border-safety-blue/40',
  },
  restoration_in_progress: {
    label: 'Restoration In Progress',
    badgeClass: 'bg-safety-cyan/20 text-safety-cyan border-safety-cyan/40',
  },
  completed_restored: {
    label: 'Completed / Show Ready',
    badgeClass: 'bg-safety-green/20 text-safety-green border-safety-green/40',
  },
};

export const PhotoLogDrawer: React.FC<PhotoLogDrawerProps> = ({
  phaseFilter,
  subAssemblyFilter,
  isOpen = true,
  onClose,
  className = '',
}) => {
  const { state, dispatch } = useRestoMod();
  const activePhase = phaseFilter || state.activePhase;

  // Local filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [filterByPhase, setFilterByPhase] = useState<boolean>(true);
  const [dossierOnly, setDossierOnly] = useState<boolean>(false);
  const [milestoneOnly, setMilestoneOnly] = useState<boolean>(false);

  // Modals & view states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState<PhotoLog | null>(null);
  const [deletingPhoto, setDeletingPhoto] = useState<PhotoLog | null>(null);
  const [inspectedPhoto, setInspectedPhoto] = useState<PhotoLog | null>(null);
  const [inspectedSide, setInspectedSide] = useState<'before' | 'after'>('before');

  // Form states for Add/Edit
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPhase, setFormPhase] = useState<RestorationPhase>(activePhase);
  const [formSubAssemblyId, setFormSubAssemblyId] = useState<string>('');
  const [formCategory, setFormCategory] = useState<PhotoCategory>('before_after_pair');
  const [formBeforeUrl, setFormBeforeUrl] = useState('');
  const [formAfterUrl, setFormAfterUrl] = useState('');
  const [formTags, setFormTags] = useState('');
  const [formIncludeInDossier, setFormIncludeInDossier] = useState<boolean>(true);
  const [formMilestoneProof, setFormMilestoneProof] = useState<boolean>(true);

  // Filtered photos
  const filteredPhotos = useMemo(() => {
    return state.photoLogs.filter((p) => {
      if (filterByPhase && p.phase !== activePhase) return false;
      if (subAssemblyFilter && p.subAssemblyId !== subAssemblyFilter) return false;
      if (selectedCategory !== 'all' && p.category !== selectedCategory) return false;
      if (dossierOnly && !p.includeInDossier) return false;
      if (milestoneOnly && !p.milestoneProof) return false;
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchesTitle = p.title.toLowerCase().includes(q);
        const matchesDesc = p.description.toLowerCase().includes(q);
        const matchesTag = p.tags.some((t) => t.toLowerCase().includes(q));
        return matchesTitle || matchesDesc || matchesTag;
      }
      return true;
    });
  }, [
    state.photoLogs,
    filterByPhase,
    activePhase,
    subAssemblyFilter,
    selectedCategory,
    dossierOnly,
    milestoneOnly,
    searchTerm,
  ]);

  // Open Add modal
  const handleOpenAddModal = () => {
    setFormTitle('');
    setFormDescription('');
    setFormPhase(activePhase);
    setFormSubAssemblyId(state.subAssemblies[0]?.id || '');
    setFormCategory('before_after_pair');
    setFormBeforeUrl('https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&w=800&q=80');
    setFormAfterUrl('https://images.unsplash.com/photo-1583121274602-3e2820c69888?auto=format&fit=crop&w=800&q=80');
    setFormTags('chassis, suspension, rebuild');
    setFormIncludeInDossier(true);
    setFormMilestoneProof(true);
    setIsAddModalOpen(true);
  };

  // Open Edit modal
  const handleOpenEditModal = (p: PhotoLog) => {
    setEditingPhoto(p);
    setFormTitle(p.title);
    setFormDescription(p.description);
    setFormPhase(p.phase);
    setFormSubAssemblyId(p.subAssemblyId || '');
    setFormCategory(p.category);
    setFormBeforeUrl(p.beforeImageUrl);
    setFormAfterUrl(p.afterImageUrl || '');
    setFormTags(p.tags.join(', '));
    setFormIncludeInDossier(p.includeInDossier);
    setFormMilestoneProof(p.milestoneProof);
  };

  // Save New Photo Log
  const handleSaveNewPhoto = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle.trim() || !formBeforeUrl.trim()) return;

    const parsedTags = formTags
      .split(',')
      .map((t) => t.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'))
      .filter(Boolean);

    const newPhoto: PhotoLog = {
      id: `photo_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      projectId: state.project.id,
      phase: formPhase,
      subAssemblyId: formSubAssemblyId || undefined,
      category: formCategory,
      title: formTitle.trim(),
      description: formDescription.trim(),
      beforeImageUrl: formBeforeUrl.trim(),
      afterImageUrl: formAfterUrl.trim() || undefined,
      capturedAt: new Date().toISOString(),
      tags: parsedTags,
      includeInDossier: formIncludeInDossier,
      milestoneProof: formMilestoneProof,
      createdAt: new Date().toISOString(),
    };

    dispatch({ type: 'ADD_PHOTO_LOG', payload: newPhoto });
    setIsAddModalOpen(false);
  };

  // Save Edit Photo Log
  const handleSaveEditPhoto = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPhoto || !formTitle.trim() || !formBeforeUrl.trim()) return;

    const parsedTags = formTags
      .split(',')
      .map((t) => t.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'))
      .filter(Boolean);

    const updated: PhotoLog = {
      ...editingPhoto,
      phase: formPhase,
      subAssemblyId: formSubAssemblyId || undefined,
      category: formCategory,
      title: formTitle.trim(),
      description: formDescription.trim(),
      beforeImageUrl: formBeforeUrl.trim(),
      afterImageUrl: formAfterUrl.trim() || undefined,
      tags: parsedTags,
      includeInDossier: formIncludeInDossier,
      milestoneProof: formMilestoneProof,
    };

    dispatch({ type: 'UPDATE_PHOTO_LOG', payload: updated });
    setEditingPhoto(null);
  };

  // Toggle Dossier inclusion directly
  const handleToggleDossier = (p: PhotoLog) => {
    dispatch({
      type: 'UPDATE_PHOTO_LOG',
      payload: {
        ...p,
        includeInDossier: !p.includeInDossier,
      },
    });
  };

  // Toggle Milestone proof directly
  const handleToggleMilestone = (p: PhotoLog) => {
    dispatch({
      type: 'UPDATE_PHOTO_LOG',
      payload: {
        ...p,
        milestoneProof: !p.milestoneProof,
      },
    });
  };

  // Confirm delete
  const handleConfirmDelete = () => {
    if (!deletingPhoto) return;
    dispatch({ type: 'DELETE_PHOTO_LOG', payload: deletingPhoto.id });
    setDeletingPhoto(null);
  };

  if (!isOpen) return null;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* 1. Header Toolbar */}
      <div className="bg-shop-panel border border-shop-border rounded p-4 shadow-machined flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        {/* Search & Filter Controls */}
        <div className="flex items-center gap-3 flex-wrap flex-1">
          {/* Search Input */}
          <div className="relative min-w-[200px] flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-shop-muted pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search photo titles, tags, notes..."
              className="w-full min-h-[44px] pl-9 pr-11 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light placeholder:text-shop-muted focus:outline-none focus:border-safety-amber font-mono"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-0 top-1/2 -translate-y-1/2 min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-shop-light cursor-pointer"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Phase Filter Toggle */}
          <button
            type="button"
            onClick={() => setFilterByPhase(!filterByPhase)}
            className={`min-h-[44px] px-3 py-2 rounded text-xs font-mono font-bold uppercase border transition-all duration-100 flex items-center gap-2 cursor-pointer ${
              filterByPhase
                ? 'bg-safety-amber text-shop-darkest border-safety-amberDark shadow-sm'
                : 'bg-shop-dark text-shop-muted border-shop-border hover:text-shop-light hover:bg-shop-well'
            }`}
          >
            <Filter className="w-3.5 h-3.5" />
            <span>{filterByPhase ? `Current Phase (${RESTORATION_PHASES[activePhase]?.shortCode})` : 'All Phases'}</span>
          </button>

          {/* Category Select */}
          <div className="relative">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light focus:outline-none focus:border-safety-amber font-mono cursor-pointer"
            >
              <option value="all">All Categories</option>
              {(Object.keys(CATEGORY_CONFIG) as PhotoCategory[]).map((cat) => (
                <option key={cat} value={cat}>
                  {CATEGORY_CONFIG[cat].label}
                </option>
              ))}
            </select>
          </div>

          {/* Dossier Curation Toggle */}
          <button
            type="button"
            onClick={() => setDossierOnly(!dossierOnly)}
            className={`min-h-[44px] px-3 py-2 rounded text-xs font-mono font-bold uppercase border transition-all flex items-center gap-1.5 cursor-pointer ${
              dossierOnly
                ? 'bg-safety-amber/20 text-safety-amber border-safety-amber'
                : 'bg-shop-dark text-shop-muted border-shop-border hover:text-shop-light'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            <span>Dossier Only</span>
          </button>

          {/* Milestone Only Toggle */}
          <button
            type="button"
            onClick={() => setMilestoneOnly(!milestoneOnly)}
            className={`min-h-[44px] px-3 py-2 rounded text-xs font-mono font-bold uppercase border transition-all flex items-center gap-1.5 cursor-pointer ${
              milestoneOnly
                ? 'bg-safety-green/20 text-safety-green border-safety-green'
                : 'bg-shop-dark text-shop-muted border-shop-border hover:text-shop-light'
            }`}
          >
            <CheckCircle2 className="w-4 h-4" />
            <span>Milestone Proofs</span>
          </button>
        </div>

        {/* Add Photo Button & Optional Close (Min 44px) */}
        <div className="flex items-center gap-2">
          <MechanicalButton
            variant="primary"
            size="default"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleOpenAddModal}
          >
            Log Photo Proof
          </MechanicalButton>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded text-shop-muted hover:text-shop-light hover:bg-shop-dark border border-shop-border transition-colors cursor-pointer"
              title="Close drawer"
              aria-label="Close drawer"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* 2. Photo Logs Cards Grid */}
      {filteredPhotos.length === 0 ? (
        <div className="bg-shop-dark border border-shop-border rounded p-12 text-center space-y-3">
          <div className="w-12 h-12 bg-shop-panel rounded-full flex items-center justify-center mx-auto text-shop-muted border border-shop-border">
            <Camera className="w-6 h-6" />
          </div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-shop-highlight">
            No Photographic Evidence Found
          </h3>
          <p className="text-xs text-shop-muted max-w-md mx-auto">
            {searchTerm || selectedCategory !== 'all' || dossierOnly || milestoneOnly
              ? 'No photos match your active filter criteria. Try resetting search or filter toggles.'
              : `No photographic records logged for Phase ${RESTORATION_PHASES[activePhase]?.sequence} yet. Document teardown routing, as-found condition, and restored components.`}
          </p>
          <div className="pt-2">
            <MechanicalButton variant="secondary" size="sm" onClick={handleOpenAddModal}>
              Log First Photo
            </MechanicalButton>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredPhotos.map((photo) => {
            const catMeta = CATEGORY_CONFIG[photo.category];
            const matchingSub = state.subAssemblies.find((s) => s.id === photo.subAssemblyId);
            const isPair = Boolean(photo.afterImageUrl);

            return (
              <div
                key={photo.id}
                className="bg-shop-dark border border-shop-border rounded overflow-hidden shadow-machined flex flex-col justify-between hover:bg-shop-panel/60 transition-all duration-150"
              >
                {/* Photo Image Viewport */}
                <div className="relative bg-shop-darkest aspect-video w-full overflow-hidden border-b border-shop-border group">
                  {isPair ? (
                    <div className="grid grid-cols-2 h-full w-full">
                      {/* Before Frame */}
                      <div className="relative h-full border-r border-shop-border/70 overflow-hidden">
                        <img
                          src={photo.beforeImageUrl}
                          alt={`${photo.title} (Before)`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                        <span className="absolute bottom-1.5 left-1.5 text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-shop-darkest/90 text-safety-amber border border-shop-border uppercase">
                          BEFORE
                        </span>
                      </div>

                      {/* After Frame */}
                      <div className="relative h-full overflow-hidden">
                        <img
                          src={photo.afterImageUrl}
                          alt={`${photo.title} (After)`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                        <span className="absolute bottom-1.5 right-1.5 text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-shop-darkest/90 text-safety-green border border-shop-border uppercase">
                          AFTER
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="relative h-full w-full">
                      <img
                        src={photo.beforeImageUrl}
                        alt={photo.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                  )}

                  {/* Top Overlay Badges */}
                  <div className="absolute top-2 left-2 flex items-center gap-1.5 flex-wrap">
                    <span
                      className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded border uppercase backdrop-blur-none bg-shop-darkest ${catMeta.badgeClass}`}
                    >
                      {catMeta.label}
                    </span>
                    <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-shop-darkest text-shop-muted border border-shop-border">
                      {RESTORATION_PHASES[photo.phase]?.shortCode}
                    </span>
                  </div>

                  {/* Zoom Inspect Button */}
                  <button
                    type="button"
                    onClick={() => {
                      setInspectedPhoto(photo);
                      setInspectedSide('before');
                    }}
                    className="absolute top-2 right-2 min-h-[44px] min-w-[44px] rounded bg-shop-darkest/90 border border-shop-border text-shop-light hover:text-safety-amber flex items-center justify-center transition-colors cursor-pointer"
                    title="Zoom & Inspect high-res"
                    aria-label="Zoom photo"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Card Content */}
                <div className="p-4 space-y-3 flex-1 flex flex-col justify-between">
                  <div className="space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="text-sm font-bold text-shop-highlight uppercase tracking-wide line-clamp-2">
                        {photo.title}
                      </h4>

                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => handleOpenEditModal(photo)}
                          className="min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-shop-highlight rounded hover:bg-shop-well transition-colors"
                          title="Edit photo metadata"
                          aria-label="Edit photo"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeletingPhoto(photo)}
                          className="min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-safety-red rounded hover:bg-shop-well transition-colors"
                          title="Delete photo record"
                          aria-label="Delete photo"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Sub-assembly reference */}
                    {matchingSub && (
                      <div className="text-xs font-mono text-safety-amber flex items-center gap-1.5">
                        <Layers className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">
                          [{matchingSub.binNumber}] {matchingSub.name}
                        </span>
                      </div>
                    )}

                    {/* Description */}
                    <p className="text-xs text-shop-muted line-clamp-2">{photo.description}</p>

                    {/* Tags */}
                    {photo.tags.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap pt-1">
                        {photo.tags.map((t) => (
                          <span
                            key={t}
                            className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-shop-darkest text-shop-muted border border-shop-border flex items-center gap-1"
                          >
                            <Tag className="w-2.5 h-2.5 text-safety-amber" />
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Bottom: Underwriting Toggles */}
                  <div className="pt-3 border-t border-shop-border grid grid-cols-2 gap-2 text-xs font-mono">
                    {/* Include in Dossier Toggle */}
                    <button
                      type="button"
                      onClick={() => handleToggleDossier(photo)}
                      className={`min-h-[44px] px-2.5 py-1.5 rounded border flex items-center justify-center gap-1.5 transition-all cursor-pointer font-bold select-none ${
                        photo.includeInDossier
                          ? 'bg-safety-amber/20 text-safety-amber border-safety-amber hover:bg-safety-amber/30'
                          : 'bg-shop-darkest text-shop-muted border-shop-border hover:text-shop-light'
                      }`}
                      title="Include in Hagerty / Grundy Agreed-Value Dossier"
                    >
                      <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                      <span className="text-[11px] truncate">
                        {photo.includeInDossier ? 'DOSSIER' : '+ DOSSIER'}
                      </span>
                    </button>

                    {/* Milestone Proof Toggle */}
                    <button
                      type="button"
                      onClick={() => handleToggleMilestone(photo)}
                      className={`min-h-[44px] px-2.5 py-1.5 rounded border flex items-center justify-center gap-1.5 transition-all cursor-pointer font-bold select-none ${
                        photo.milestoneProof
                          ? 'bg-safety-green/20 text-safety-green border-safety-green hover:bg-safety-green/30'
                          : 'bg-shop-darkest text-shop-muted border-shop-border hover:text-shop-light'
                      }`}
                      title="Mark as milestone completion proof"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                      <span className="text-[11px] truncate">
                        {photo.milestoneProof ? 'PROOF' : '+ PROOF'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* =========================================================================
          3. FULLSCREEN LIGHTBOX INSPECTOR
         ========================================================================= */}
      {inspectedPhoto && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col p-4">
          <div className="flex items-center justify-between border-b border-shop-border pb-3 shrink-0">
            <div>
              <h3 className="text-sm font-bold text-shop-highlight uppercase tracking-wider font-mono">
                {inspectedPhoto.title}
              </h3>
              <p className="text-xs text-shop-muted font-mono">{inspectedPhoto.description}</p>
            </div>
            <div className="flex items-center gap-2">
              {inspectedPhoto.afterImageUrl && (
                <div className="flex border border-shop-border rounded overflow-hidden mr-2">
                  <button
                    type="button"
                    onClick={() => setInspectedSide('before')}
                    className={`min-h-[44px] px-3 font-mono text-xs font-bold uppercase transition-colors cursor-pointer ${
                      inspectedSide === 'before'
                        ? 'bg-safety-amber text-shop-darkest'
                        : 'bg-shop-dark text-shop-muted hover:text-shop-light'
                    }`}
                  >
                    Before
                  </button>
                  <button
                    type="button"
                    onClick={() => setInspectedSide('after')}
                    className={`min-h-[44px] px-3 font-mono text-xs font-bold uppercase transition-colors cursor-pointer ${
                      inspectedSide === 'after'
                        ? 'bg-safety-green text-shop-darkest'
                        : 'bg-shop-dark text-shop-muted hover:text-shop-light'
                    }`}
                  >
                    After
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => setInspectedPhoto(null)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-light hover:text-safety-amber"
                aria-label="Close lightbox"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center p-4 overflow-hidden relative">
            <img
              src={
                inspectedSide === 'after' && inspectedPhoto.afterImageUrl
                  ? inspectedPhoto.afterImageUrl
                  : inspectedPhoto.beforeImageUrl
              }
              alt={inspectedPhoto.title}
              className="max-h-full max-w-full object-contain rounded border border-shop-border shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* =========================================================================
          4. ADD PHOTO LOG MODAL
         ========================================================================= */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-shop-dark border-2 border-safety-amber rounded max-w-xl w-full p-6 space-y-4 shadow-2xl relative my-8">
            <div className="flex items-center justify-between border-b border-shop-border pb-3">
              <div className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-safety-amber" />
                <h3 className="text-sm font-bold uppercase tracking-wider text-shop-highlight">
                  Log Photographic Evidence
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-shop-highlight"
                aria-label="Close dialog"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveNewPhoto} className="space-y-4 text-xs font-mono">
              {/* Title */}
              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Photo Title / Caption *</label>
                <input
                  type="text"
                  required
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="e.g. Front K-Member Before vs After Powder Coat"
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light font-bold focus:border-safety-amber focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Phase */}
                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Restoration Phase *</label>
                  <select
                    value={formPhase}
                    onChange={(e) => setFormPhase(e.target.value as RestorationPhase)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {Object.keys(RESTORATION_PHASES).map((pk) => {
                      const p = pk as RestorationPhase;
                      return (
                        <option key={p} value={p}>
                          Phase {RESTORATION_PHASES[p].sequence}: {RESTORATION_PHASES[p].title}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Category */}
                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Evidence Category *</label>
                  <select
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value as PhotoCategory)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {(Object.keys(CATEGORY_CONFIG) as PhotoCategory[]).map((cat) => (
                      <option key={cat} value={cat}>
                        {CATEGORY_CONFIG[cat].label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Linked SubAssembly */}
              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Linked Sub-Assembly Container</label>
                <select
                  value={formSubAssemblyId}
                  onChange={(e) => setFormSubAssemblyId(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                >
                  <option value="">None / General Vehicle</option>
                  {state.subAssemblies.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      [{sub.binNumber}] {sub.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Before Image URL */}
              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">
                  Primary / Before Image URL *
                </label>
                <input
                  type="text"
                  required
                  value={formBeforeUrl}
                  onChange={(e) => setFormBeforeUrl(e.target.value)}
                  placeholder="https://... or data:image/..."
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                />
              </div>

              {/* After Image URL (Optional) */}
              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">
                  Restored / After Image URL (Optional Pairing)
                </label>
                <input
                  type="text"
                  value={formAfterUrl}
                  onChange={(e) => setFormAfterUrl(e.target.value)}
                  placeholder="https://... or data:image/..."
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                />
              </div>

              {/* Tags */}
              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">
                  Tags (Comma separated)
                </label>
                <input
                  type="text"
                  value={formTags}
                  onChange={(e) => setFormTags(e.target.value)}
                  placeholder="e.g. k_member, powder_coat, suspension, before_after"
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                />
              </div>

              {/* Description */}
              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Technical Description</label>
                <textarea
                  rows={2}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Details of disassembly routing or restored tolerances..."
                  className="w-full px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none font-sans"
                />
              </div>

              {/* Underwriting Toggles */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <label className="flex items-center gap-2 cursor-pointer select-none text-shop-light min-h-[44px]">
                  <input
                    type="checkbox"
                    checked={formIncludeInDossier}
                    onChange={(e) => setFormIncludeInDossier(e.target.checked)}
                    className="w-4 h-4 rounded border-shop-border text-safety-amber focus:ring-0 cursor-pointer"
                  />
                  <span>Feature in Valuation Dossier</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none text-shop-light min-h-[44px]">
                  <input
                    type="checkbox"
                    checked={formMilestoneProof}
                    onChange={(e) => setFormMilestoneProof(e.target.checked)}
                    className="w-4 h-4 rounded border-shop-border text-safety-green focus:ring-0 cursor-pointer"
                  />
                  <span>Milestone Completion Proof</span>
                </label>
              </div>

              {/* Actions */}
              <div className="pt-2 flex justify-end gap-3 border-t border-shop-border">
                <MechanicalButton
                  type="button"
                  variant="outline"
                  size="default"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </MechanicalButton>
                <MechanicalButton type="submit" variant="primary" size="default">
                  Save Photo Proof
                </MechanicalButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================================
          5. EDIT PHOTO LOG MODAL
         ========================================================================= */}
      {editingPhoto && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-shop-dark border-2 border-shop-border rounded max-w-xl w-full p-6 space-y-4 shadow-2xl relative my-8">
            <div className="flex items-center justify-between border-b border-shop-border pb-3">
              <div className="flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-safety-amber" />
                <h3 className="text-sm font-bold uppercase tracking-wider text-shop-highlight">
                  Edit Photo Proof
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setEditingPhoto(null)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-shop-highlight"
                aria-label="Close dialog"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveEditPhoto} className="space-y-4 text-xs font-mono">
              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Photo Title *</label>
                <input
                  type="text"
                  required
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light font-bold focus:border-safety-amber focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Restoration Phase *</label>
                  <select
                    value={formPhase}
                    onChange={(e) => setFormPhase(e.target.value as RestorationPhase)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {Object.keys(RESTORATION_PHASES).map((pk) => {
                      const p = pk as RestorationPhase;
                      return (
                        <option key={p} value={p}>
                          Phase {RESTORATION_PHASES[p].sequence}: {RESTORATION_PHASES[p].title}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Evidence Category *</label>
                  <select
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value as PhotoCategory)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {(Object.keys(CATEGORY_CONFIG) as PhotoCategory[]).map((cat) => (
                      <option key={cat} value={cat}>
                        {CATEGORY_CONFIG[cat].label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Before Image URL *</label>
                <input
                  type="text"
                  required
                  value={formBeforeUrl}
                  onChange={(e) => setFormBeforeUrl(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">After Image URL (Optional)</label>
                <input
                  type="text"
                  value={formAfterUrl}
                  onChange={(e) => setFormAfterUrl(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Tags</label>
                <input
                  type="text"
                  value={formTags}
                  onChange={(e) => setFormTags(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Description</label>
                <textarea
                  rows={2}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  className="w-full px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none font-sans"
                />
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <label className="flex items-center gap-2 cursor-pointer select-none text-shop-light min-h-[44px]">
                  <input
                    type="checkbox"
                    checked={formIncludeInDossier}
                    onChange={(e) => setFormIncludeInDossier(e.target.checked)}
                    className="w-4 h-4 rounded border-shop-border text-safety-amber focus:ring-0 cursor-pointer"
                  />
                  <span>Feature in Valuation Dossier</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none text-shop-light min-h-[44px]">
                  <input
                    type="checkbox"
                    checked={formMilestoneProof}
                    onChange={(e) => setFormMilestoneProof(e.target.checked)}
                    className="w-4 h-4 rounded border-shop-border text-safety-green focus:ring-0 cursor-pointer"
                  />
                  <span>Milestone Completion Proof</span>
                </label>
              </div>

              <div className="pt-2 flex justify-end gap-3 border-t border-shop-border">
                <MechanicalButton
                  type="button"
                  variant="outline"
                  size="default"
                  onClick={() => setEditingPhoto(null)}
                >
                  Cancel
                </MechanicalButton>
                <MechanicalButton type="submit" variant="primary" size="default">
                  Save Changes
                </MechanicalButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================================
          6. DELETE CONFIRMATION MODAL
         ========================================================================= */}
      {deletingPhoto && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-shop-dark border-2 border-safety-red rounded max-w-md w-full p-6 space-y-4 shadow-2xl">
            <h3 className="text-base font-bold uppercase tracking-wider text-safety-red">
              Delete Photographic Proof?
            </h3>
            <p className="text-xs text-shop-light leading-relaxed">
              Are you sure you want to remove photo &quot;{deletingPhoto.title}&quot; from the evidence chain?
              This will also remove it from underwriting dossier curation.
            </p>
            <div className="pt-2 flex justify-end gap-3 border-t border-shop-border">
              <MechanicalButton
                type="button"
                variant="outline"
                size="default"
                onClick={() => setDeletingPhoto(null)}
              >
                Cancel
              </MechanicalButton>
              <MechanicalButton
                type="button"
                variant="danger"
                size="default"
                onClick={handleConfirmDelete}
              >
                Delete Photo
              </MechanicalButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
